import React, { useState, useEffect } from "react";
import { Modal, ActionIcon, ScrollArea } from "@mantine/core";
import { IconX, IconUserPlus, IconCalendarTime } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";
import Searchbar from "../Searchbar/Searchbar";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import TableLoader from "../Table Loader/TableLoader";
import EmptyList from "../Empty List/EmptyList";
import ApiService from "../../services/api";
import fireToast from "../../helpers/toaster";
import { PATHS } from "../../constants/paths";
import styles from "./SelectClientModal.module.scss";

const ADD_PATIENT_MODAL_EVENT = "openAddPatientModal";

const SelectClientModal = ({ isOpen, onClose, selectedSlot }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [search] = useDebouncedValue(searchValue, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const fetchClients = () => {
    if (!isOpen) return;
    setLoading(true);
    ApiService.getOpdPatients({
      page: currentPage,
      limit: perPage,
      search: search || undefined,
      status: "all",
    })
      .then((response) => {
        setClients(response.data.data || []);
        setCount(response.data.total || 0);
        setLoading(false);
      })
      .catch((error) => {
        if (error.response) {
          fireToast("error", error.response.data.message, "bottom-right", true);
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      setSearchValue("");
      setCurrentPage(1);
      fetchClients();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [search, currentPage]);

  const handleSelectClient = (patient) => {
    onClose();
    navigate(PATHS.ADD_RECEPTIONIST_APPOINTMENT, {
      state: {
        patientData: patient,
        selectedSlot: selectedSlot,
      },
    });
  };

  const handleCreatePatient = () => {
    onClose();
    window.dispatchEvent(new CustomEvent(ADD_PATIENT_MODAL_EVENT));
  };

  const hasResults = clients?.length > 0;
  const hasSearched = search.length > 0;
  const showEmptyState = !loading && !hasResults;

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      padding="0px"
      withCloseButton={false}
      size="md"
      classNames={{ content: styles.modal_content }}
    >
      <div className={styles.select_client_modal}>
        <div className={styles.modal_header}>
          <h2 className={styles.title}>Select Patient</h2>
          <ActionIcon variant="light" onClick={onClose}>
            <IconX style={{ width: "75%", height: "75%" }} />
          </ActionIcon>
        </div>

        <p className={styles.subtitle}>
          Search and select a patient to create an appointment
        </p>

        <div className={styles.search_wrapper}>
          <Searchbar
            placeholder="Search patients by name, ID, email..."
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </div>

        <div className={styles.list_section}>
          {loading ? (
            <div className={styles.loader_wrapper}>
              <TableLoader noOftd={5} />
            </div>
          ) : showEmptyState ? (
            <div className={styles.empty_wrapper}>
              <EmptyList
                text={
                  hasSearched
                    ? "No patients found. Try a different search."
                    : "No patients yet!"
                }
              />
              <PrimaryButton
                text="Create Patient"
                leftSection={<IconUserPlus size={18} />}
                onClick={handleCreatePatient}
                className={styles.create_patient_btn}
              />
            </div>
          ) : (
            <ScrollArea h={320} type="scroll">
              <div className={styles.client_list}>
                {clients.map((client, i) => (
                  <div
                    key={client.patientId || client.userId || i}
                    className={styles.client_item}
                    onClick={() => handleSelectClient(client)}
                  >
                    <div className={styles.client_info}>
                      <p className={styles.client_name}>
                        {client.patientName || "—"}
                      </p>
                      <p className={styles.client_id}>
                        {client.patientId}
                        {client.mobileNo && ` · ${client.mobileNo}`}
                      </p>
                    </div>
                    <IconCalendarTime
                      size={18}
                      className={styles.select_icon}
                      color="var(--icon-accent)"
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {hasResults && (
          <div className={styles.footer}>
            <PrimaryButton
              text="Create New Patient"
              variant="outline"
              leftSection={<IconUserPlus size={18} />}
              onClick={handleCreatePatient}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SelectClientModal;
export { ADD_PATIENT_MODAL_EVENT };
